/**
 * movement.ts — the locomotion step, in `shared` because BOTH ends run it now.
 *
 * The server owns the truth: it steps this and then resolves the result against
 * Rapier, and what the character controller actually achieved is what the
 * snapshot carries. The client steps the identical functions to predict its own
 * player (AGENTS.md: the local player is predicted), so the only thing the two can disagree about
 * is collision — walls and other capsules — which reconciliation corrects.
 *
 * That is the whole reason this file exists rather than the client keeping its
 * own copy of the maths: a second implementation of "how fast does a backpedal
 * go" is a permanent, invisible mispredict on every step.
 *
 * Nothing here touches Rapier or the ECS. Pure functions on plain data.
 */
import { ARENA } from './arena.js';
import { FEEL } from './feel.js';
import { wrapAngle } from './protocol.js';
const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
/** Unit forward for a yaw. yaw 0 faces -Z; +yaw turns counter-clockwise from above. */
export function forwardVector(yaw) {
    return { x: -Math.sin(yaw), y: 0, z: -Math.cos(yaw) };
}
/** Unit right for a yaw (forward rotated -90° about +Y). */
export function rightVector(yaw) {
    return { x: Math.cos(yaw), y: 0, z: -Math.sin(yaw) };
}
/**
 * Rotates `current` towards `desired` by at most `turnCap` rad/s. The cap is what
 * stops spin-to-win during an active window (AGENTS.md: the turn cap in an active window is a ceiling), so the client
 * must predict yaw through the same clamp or its own body would out-turn the
 * server's and every swing would leave the blade in the wrong place.
 */
export function stepYaw(current, desired, turnCap, dt) {
    const delta = wrapAngle(desired - current);
    const maxStep = turnCap * dt;
    return wrapAngle(current + clamp(delta, -maxStep, maxStep));
}
/** The velocity the player is asking for, in world space, before collision. */
export function desiredVelocity(yaw, forward, right, moveSpeed) {
    const f = forwardVector(yaw);
    const r = rightVector(yaw);
    let x = f.x * forward + r.x * right;
    let z = f.z * forward + r.z * right;
    const length = Math.hypot(x, z);
    if (length < 1e-6)
        return { x: 0, y: 0, z: 0 };
    // Normalize so diagonals are not faster, then scale to the allowed speed.
    // Moving backwards is slower: footwork is a real decision.
    const speed = moveSpeed * (forward < 0 ? FEEL.BACKPEDAL_MULT : 1);
    x = (x / length) * speed;
    z = (z / length) * speed;
    return { x, y: 0, z };
}
/** Moves `current` towards `target` by at most `rate * dt`, componentwise in the plane. */
export function approachVelocity(current, target, rate, dt) {
    const dx = target.x - current.x;
    const dz = target.z - current.z;
    const distance = Math.hypot(dx, dz);
    const step = rate * dt;
    if (distance <= step || distance < 1e-9)
        return { x: target.x, y: 0, z: target.z };
    return { x: current.x + (dx / distance) * step, y: 0, z: current.z + (dz / distance) * step };
}
/**
 * The move-speed multiplier for what the player is currently doing. Attacking and
 * blocking both slow the feet; they are mutually exclusive, and attacking wins.
 *
 * `statusSystem` applies this to the effective stat and the client's predictor
 * applies it to its local copy — one definition, so a swing does not visibly
 * slow you at a different moment on each end.
 */
export function actionMoveMult(state) {
    if (state.attacking)
        return FEEL.MOVE_MULT_ATTACKING;
    if (state.blocking)
        return FEEL.MOVE_MULT_BLOCKING;
    return 1;
}
/**
 * The turn cap during an active attack window: a ceiling, not a modifier, so it
 * cannot be escaped by stacking buffs (AGENTS.md: the turn cap in an active window is a ceiling).
 */
export function actionTurnCap(base, inActiveWindow) {
    return inActiveWindow ? Math.min(base, FEEL.TURN_CAP_ACTIVE) : base;
}
/**
 * One fixed-timestep locomotion step, WITHOUT collision.
 *
 * Returns a fresh state; nothing is mutated, because the client replays this over
 * the same command list repeatedly and an in-place step would corrupt the history
 * it is replaying from.
 *
 * The server runs the same yaw/accel maths and then hands the planned motion to
 * the character controller, so this is the truth right up to the point a wall or
 * another player is in the way.
 */
export function stepLocomotion(state, input, stats, dt) {
    const yaw = stepYaw(state.yaw, input.yaw, stats.turnCap, dt);
    const target = desiredVelocity(yaw, input.f, input.r, stats.moveSpeed);
    const accelerating = target.x !== 0 || target.z !== 0;
    const rate = accelerating ? FEEL.ACCEL : FEEL.FRICTION;
    const vel = approachVelocity(state.vel, target, rate, dt);
    return {
        pos: { x: state.pos.x + vel.x * dt, y: state.pos.y, z: state.pos.z + vel.z * dt },
        yaw,
        vel,
    };
}
/**
 * Keeps a capsule centre inside the arena walls.
 *
 * This is the client's entire collision model, and deliberately so: Rapier is
 * server-side only (AGENTS.md stack), so the predictor approximates the one case
 * that happens constantly — running at a wall — and simply mispredicts the rare
 * one, player-vs-player contact, which reconciliation smooths out. Without this,
 * holding W into a wall would slide the predicted body straight through it and
 * then yank it back sixty times a second.
 */
export function clampToArena(pos) {
    const limit = ARENA.size / 2 - FEEL.BODY_RADIUS;
    return { x: clamp(pos.x, -limit, limit), y: pos.y, z: clamp(pos.z, -limit, limit) };
}
/** Whether a ground speed reads as running rather than standing. */
export const RUN_SPEED_THRESHOLD = 0.15;
/**
 * Advances the idle/run cycle for a ground speed. `movementSystem` drives the
 * server's locomotion animation with this and the client predicts its own with
 * the same call, so your own legs do not change cadence when a snapshot lands.
 */
export function stepLocomotionAnim(current, speed, dt) {
    const moving = speed > RUN_SPEED_THRESHOLD;
    const id = moving ? 'run' : 'idle';
    if (current.id !== id)
        return { id, t: 0 };
    // Locomotion loops; cycle rate scales with speed so runs do not skate.
    const cycleHz = moving ? speed / 2.2 : 0.4;
    return { id, t: (current.t + cycleHz * dt) % 1 };
}
//# sourceMappingURL=movement.js.map